// apps/client/src/boot/install.ts (full screen, 2026-10-15; spec §8.8) — how this page gets to full screen. An iPhone never lets a page
// go full screen: only the home-screen app does (Add to Home Screen from the Share sheet; iOS has no install prompt). Android and desktop
// Chrome offer an install prompt (captured in boot/pwa.ts) and the Fullscreen API, which Play now asks for inside its tap. Pure helpers
// over a window-shaped argument, so tests pass fakes; ui/install.tsx shows the title's tip and the settings entry.
// Full screen button (2026-10-18): `fullscreenWay()` is the one way the Full screen button takes (ui/fullscreen.tsx), found by feature
// detection rather than the user agent, so an iPad (whose Safari has element full screen) gets the real thing.

export interface InstallNavigator { userAgent: string; maxTouchPoints?: number; standalone?: boolean }
type RequestOptions = { navigationUI?: 'hide' | 'show' | 'auto' };
/** The element Fullscreen API, standard or (older iPadOS Safari) webkit-prefixed. iPhone Safari has neither. */
export interface FullscreenDocument {
  fullscreenEnabled?: boolean;
  fullscreenElement?: unknown;
  exitFullscreen?(): Promise<void>;
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: unknown;
  webkitExitFullscreen?(): void;
  documentElement: { requestFullscreen?(opts?: RequestOptions): Promise<void>; webkitRequestFullscreen?(): void | Promise<void> };
}
export interface FullscreenWindow {
  navigator: InstallNavigator;
  matchMedia(q: string): { matches: boolean };
  document: FullscreenDocument;
  screen?: { orientation?: { lock?(o: string): Promise<void> } };
}
/** 'installed': opened from the home screen. 'ios': Add to Home Screen from the Share sheet. 'prompt': the browser's own install prompt
 *  is ready. 'menu': none of these; the browser's menu may still have Install app or Add to Home Screen. */
export type InstallWay = 'installed' | 'ios' | 'prompt' | 'menu';

/** An iPhone, iPod or iPad, including an iPad asking for desktop sites (a Mac user agent with a touch screen; Macs have none). */
export function isIos(nav: InstallNavigator): boolean {
  return /iPhone|iPad|iPod/.test(nav.userAgent) || (/Macintosh/.test(nav.userAgent) && (nav.maxTouchPoints ?? 0) > 1);
}
/** iOS browsers with Add to Home Screen in their Share sheet (Safari, and since iOS 16.4 Chrome, Firefox and Edge, all of which keep the
 *  `Safari/` token); an app's built-in browser (Instagram, Facebook, Google) has neither the token nor the menu item. */
export function canAddToHomeScreen(nav: InstallNavigator): boolean {
  return isIos(nav) && /Safari\//.test(nav.userAgent) && !/FBAN|FBAV|Instagram|GSA\/|Line\//.test(nav.userAgent);
}
/** Opened from the home screen. A browser tab that went full screen through the Fullscreen API also matches display-mode fullscreen, so
 *  a document with a fullscreen element is a tab, not the installed app. */
export function isInstalled(win: Pick<FullscreenWindow, 'navigator' | 'matchMedia' | 'document'>): boolean {
  if (win.navigator.standalone === true || win.matchMedia('(display-mode: standalone)').matches) return true;
  return win.matchMedia('(display-mode: fullscreen)').matches && !isFullscreen(win.document);
}
/** The page is in element full screen (standard or webkit-prefixed). */
export function isFullscreen(doc: FullscreenDocument): boolean {
  return Boolean(doc.fullscreenElement ?? doc.webkitFullscreenElement);
}
/** The request for this page's element full screen, or null where the browser has none or switched it off (an iframe without
 *  allowfullscreen). Feature detection only: iPhone Safari has no element full screen (only <video>, through Apple's own player, which
 *  keeps every touch from the page), an iPad's Safari has it. */
function fullscreenRequest(doc: FullscreenDocument): (() => void | Promise<void>) | null {
  const el = doc.documentElement;
  if (doc.fullscreenEnabled === true && typeof el.requestFullscreen === 'function') return () => el.requestFullscreen!({ navigationUI: 'hide' });
  if (doc.webkitFullscreenEnabled === true && typeof el.webkitRequestFullscreen === 'function') return () => el.webkitRequestFullscreen!();
  return null;
}
/** How the Full screen button takes this page full screen (ui/fullscreen.tsx). 'api': the element Fullscreen API (Android, desktop, an
 *  iPad). 'homeScreen': Add to Home Screen, the only full screen an iPhone gives a page (its browsers have no element API); the button
 *  opens a short guide. 'none': already full screen or installed, or nothing is possible here.
 *  A future way slots in here: a 'swipe' way, should /fullscreen-test show iPhone Safari's bars stay hidden after a swipe up in landscape,
 *  is one more check below (before 'homeScreen') and one more case in ui/fullscreen.tsx goFullscreen. */
export type FullscreenWay = 'api' | 'homeScreen' | 'none';
export function fullscreenWay(win: FullscreenWindow = window as unknown as FullscreenWindow): FullscreenWay {
  if (isFullscreen(win.document) || isInstalled(win)) return 'none';
  if (fullscreenRequest(win.document)) return 'api';
  return canAddToHomeScreen(win.navigator) ? 'homeScreen' : 'none';
}
export function installWay(s: { installed: boolean; homeScreen: boolean; canPrompt: boolean }): InstallWay {
  if (s.installed) return 'installed';
  if (s.homeScreen) return 'ios';
  return s.canPrompt ? 'prompt' : 'menu';
}
/** The title's tip: only where one tap (or two) installs, and until the player hides it. */
export function showInstallHint(way: InstallWay, dismissed: boolean): boolean {
  return !dismissed && (way === 'ios' || way === 'prompt');
}

type KeyStore = { getItem(k: string): string | null; setItem(k: string, v: string): void } | null | undefined;
export const INSTALL_HINT_KEY = 'wt:installHintDismissed';
const storage = (): KeyStore => { try { return globalThis.localStorage; } catch { return null; } };
export function installHintDismissed(store: KeyStore = storage()): boolean {
  try { return store?.getItem(INSTALL_HINT_KEY) === '1'; } catch { return false; }
}
export function dismissInstallHint(store: KeyStore = storage()): void {
  try { store?.setItem(INSTALL_HINT_KEY, '1'); } catch { /* private mode / no storage: the tip comes back next launch */ }
}

/** Play now and the Full screen button, inside their tap: where the way is 'api' (Android, a desktop, an iPad), asks to go full screen
 *  without the navigation bar, then to lock landscape (allowed only once full screen; an iPad refuses). Not on an iPhone (no element
 *  full screen), not in the installed app or once full screen. Any refusal is silent: the game plays the same in a tab. */
export function enterFullscreen(win: FullscreenWindow = window as unknown as FullscreenWindow): 'requested' | 'skipped' {
  const request = fullscreenWay(win) === 'api' ? fullscreenRequest(win.document) : null;
  if (!request) return 'skipped';
  let pending: void | Promise<void>;
  try { pending = request(); } catch { return 'skipped'; }
  void Promise.resolve(pending).then(() => win.screen?.orientation?.lock?.('landscape')).catch(() => {});
  return 'requested';
}
/** The in-town Menu's Exit full screen: leaves element full screen (the landscape lock goes with it). Silent when refused. */
export function exitFullscreen(win: FullscreenWindow = window as unknown as FullscreenWindow): void {
  const doc = win.document;
  if (!isFullscreen(doc)) return;
  try {
    const left = doc.exitFullscreen ? doc.exitFullscreen() : doc.webkitExitFullscreen?.();
    void Promise.resolve(left).catch(() => {});
  } catch { /* not full screen after all */ }
}
